/**
 * Comparer des fournisseurs sur des faits (V1.6-11).
 *
 * **Aucun classement global, et c'est le sujet.** Un tableau trié par « score »
 * déciderait à la place de celui qui achète : un fournisseur peut être lent et
 * le moins cher, ou rapide et incomplet, et laquelle de ces qualités compte
 * dépend de ce qu'on achète ce jour-là. Les colonnes se lisent séparément, dans
 * l'ordre alphabétique des noms — un ordre qui n'affirme rien.
 *
 * Un tiret veut dire « on ne sait pas ». Jamais zéro.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Truck } from "lucide-react";

import { useSupplierComparison } from "@/api/supplier-intelligence";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/States";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { MetricValue } from "./MetricValue";

const PERIODS: { label: string; months: number | null }[] = [
  { label: "12 derniers mois", months: 12 },
  { label: "3 derniers mois", months: 3 },
  { label: "Depuis toujours", months: null },
];

function since(months: number | null): { date_from?: string } {
  if (months === null) return {};
  const from = new Date();
  from.setMonth(from.getMonth() - months);
  return { date_from: from.toISOString() };
}

export function SupplierComparisonPage() {
  const [months, setMonths] = useState<number | null>(12);
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, error, refetch } = useSupplierComparison({
    ...since(months),
    ...(includeInactive ? { include_inactive: "true" } : {}),
  });

  return (
    <Page>
      <PageHeader
        title="Comparer les fournisseurs"
        subtitle="Des colonnes objectives, pas une note. Ce qui compte dépend de ce que vous achetez."
        crumbs={[{ label: "Fournisseurs", to: "/suppliers" }, { label: "Comparer" }]}
      />

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {PERIODS.map((item) => (
          <button
            key={item.label}
            type="button"
            aria-pressed={months === item.months}
            onClick={() => setMonths(item.months)}
            className={`rounded-md border px-2.5 py-1 text-[12px] ${
              months === item.months
                ? "border-brand/40 bg-brand/10 text-brand"
                : "border-border text-ink-muted hover:text-ink"
            }`}
          >
            {item.label}
          </button>
        ))}
        <label className="ml-2 flex items-center gap-1.5 text-[12px] text-ink-muted">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Inclure les fournisseurs retirés
        </label>
      </div>

      <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-surface">
        {isLoading && <TableSkeleton rows={8} columns={8} />}
        {error && <ErrorState error={error} onRetry={() => void refetch()} />}

        {data && data.rows.length === 0 && (
          <EmptyState
            icon={<Truck className="size-6" aria-hidden />}
            title="Aucun fournisseur à comparer"
            description="Les fournisseurs apparaissent ici dès qu'on leur a demandé un prix ou passé une commande."
          />
        )}

        {data && data.rows.length > 0 && (
          <TableShell>
            <thead>
              <tr>
                <Th className="max-w-0">Fournisseur</Th>
                <Th align="right" className="whitespace-nowrap">
                  Réponse
                </Th>
                <Th align="right" className="hidden whitespace-nowrap @3xl:table-cell">
                  Délai réponse
                </Th>
                <Th align="right" className="whitespace-nowrap">
                  Remplissage
                </Th>
                <Th align="right" className="hidden whitespace-nowrap @4xl:table-cell">
                  Complétion
                </Th>
                <Th align="right" className="hidden whitespace-nowrap @4xl:table-cell">
                  Délai réel
                </Th>
                <Th align="right" className="hidden whitespace-nowrap @5xl:table-cell">
                  Ponctualité
                </Th>
                <Th align="right" className="hidden whitespace-nowrap @5xl:table-cell">
                  Retours
                </Th>
                <Th align="right" className="whitespace-nowrap">
                  Moins cher
                </Th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <Tr key={row.supplier_id}>
                  <Td className="max-w-0">
                    <Link
                      to={`/suppliers/${row.supplier_id}`}
                      className="block truncate font-medium text-ink hover:text-brand"
                    >
                      {row.supplier_name}
                    </Link>
                    <span className="block text-[11px] text-ink-subtle">
                      {row.purchase_orders} commandes
                      {row.active ? "" : " · retiré"}
                    </span>
                  </Td>
                  <Td align="right">
                    <MetricValue metric={row.rfq_response_rate} kind="rate" />
                    <span className="block text-[11px] text-ink-subtle">
                      {row.rfqs_responded}/{row.rfqs_requested}
                    </span>
                  </Td>
                  <Td align="right" className="hidden @3xl:table-cell">
                    <MetricValue metric={row.median_response_hours} kind="hours" />
                  </Td>
                  <Td align="right">
                    <MetricValue metric={row.gross_fill_rate} kind="rate" />
                  </Td>
                  <Td align="right" className="hidden @4xl:table-cell">
                    <MetricValue metric={row.effective_completion_rate} kind="rate" />
                  </Td>
                  <Td align="right" className="hidden @4xl:table-cell">
                    <MetricValue metric={row.average_first_receipt_lead_days} kind="days" />
                  </Td>
                  <Td align="right" className="hidden @5xl:table-cell">
                    <MetricValue metric={row.on_time_delivery_rate} kind="rate" />
                  </Td>
                  <Td
                    align="right"
                    className="tnum hidden text-[13px] text-ink-muted @5xl:table-cell"
                  >
                    {row.supplier_returns_count}
                  </Td>
                  <Td align="right">
                    <MetricValue metric={row.cheapest_known_rate} kind="rate" />
                    <span className="block text-[11px] text-ink-subtle">
                      {row.known_comparisons} comparaisons
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </div>

      <p className="mt-3 text-[12px] text-ink-subtle">
        Un tiret veut dire « pas assez de données », jamais zéro. « Moins cher » ne se calcule
        que sur les demandes où au moins deux fournisseurs ont chiffré le même article, dans la
        même devise et une unité convertible ; à égalité, tous comptent. Ce sont des prix
        marchandise, pas des coûts rendus.
      </p>
    </Page>
  );
}
